import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Alert,
  Checkbox,
  useTheme,
  useMediaQuery,
  Snackbar,
} from "@mui/material";
import { Inventory as InventoryIcon } from "@mui/icons-material";
import { apiClient } from "../services/apiClient";
import { RentalObject, RentalObjectBundle } from "../types/api-types";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import CreateBundleArticleModal from "../components/bundles/CreateBundleArticleModal";
import BundleListCard from "../components/bundles/BundleListCard";
import ObjectSelectionBar from "../components/bundles/ObjectSelectionBar";
import EditBundleArticleModal from "../components/bundles/EditBundleArticleModal";

/**
 * Bundle Management Page - Professionelle Bundle-Verwaltung
 * Optimiert für das Erstellen von Bundle-Artikeln und deren Zuordnung zu Mietobjekten
 */
const BundleManagementPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // State
  const [rentalObjects, setRentalObjects] = useState<RentalObject[]>([]);
  const [selectedObjects, setSelectedObjects] = useState<Set<string>>(
    new Set()
  );
  const [bundleArticles, setBundleArticles] = useState<RentalObjectBundle[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBundle, setEditingBundle] = useState<RentalObjectBundle | null>(
    null
  );
  const [editingObjectId, setEditingObjectId] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Lade aktive Rental Objects
      const objectsResponse = await apiClient.getRentalObjects({
        isActive: true,
      });
      const objects = Array.isArray(objectsResponse) ? objectsResponse : [];
      setRentalObjects(objects);

      // Lade Bundle-Artikel für alle Objekte
      await loadBundleArticlesForAll(objects);
    } catch (err: any) {
      console.error("Error loading data:", err);
      setError(`Fehler beim Laden der Daten: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadBundleArticlesForAll = async (objects: RentalObject[]) => {
    try {
      const allBundles: RentalObjectBundle[] = [];

      for (const object of objects) {
        try {
          const bundles = await apiClient.getBundleArticlesForObject(object.id);
          if (Array.isArray(bundles)) {
            allBundles.push(...bundles);
          }
        } catch (error) {
          console.warn(
            `Could not load bundles for object ${object.id}:`,
            error
          );
        }
      }

      setBundleArticles(allBundles);
    } catch (error) {
      console.error("Error loading bundle articles:", error);
      setBundleArticles([]);
    }
  };

  // Object selection handlers
  const handleObjectSelect = (objectId: string, checked: boolean) => {
    const newSelected = new Set(selectedObjects);
    if (checked) {
      newSelected.add(objectId);
    } else {
      newSelected.delete(objectId);
    }
    setSelectedObjects(newSelected);
  };

  const handleSelectAll = () => {
    if (
      selectedObjects.size === filteredObjects.length &&
      filteredObjects.length > 0
    ) {
      setSelectedObjects(new Set());
    } else {
      setSelectedObjects(new Set(filteredObjects.map((obj) => obj.id)));
    }
  };

  const handleClearSelection = () => {
    setSelectedObjects(new Set());
  };

  // Bundle actions
  const handleCreateBundleSuccess = async (articleId: string) => {
    setSuccessMessage("Bundle-Artikel erfolgreich erstellt und hinzugefügt!");
    setSelectedObjects(new Set());
    await loadData();
  };

  const handleRemoveBundle = async (bundleId: string, objectId: string) => {
    try {
      await apiClient.removeBundleArticleFromObject(objectId, bundleId);
      setSuccessMessage("Bundle-Artikel erfolgreich entfernt!");
      await loadData();
    } catch (error: any) {
      console.error("Error removing bundle article:", error);
      setError(`Fehler beim Entfernen: ${error.message}`);
    }
  };

  const handleEditBundle = (bundle: RentalObjectBundle) => {
    setEditingBundle(bundle);
    setEditingObjectId(bundle.rentalObjectId);
    setShowEditModal(true);
  };

  const handleEditSuccess = () => {
    setSuccessMessage("Bundle-Artikel erfolgreich aktualisiert");
    loadData(); // Reload data to reflect changes
  };

  // Filter objects
  const filteredObjects = rentalObjects.filter(
    (object) =>
      object.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      object.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get object bundles helper
  const getObjectBundles = (objectId: string) => {
    return bundleArticles.filter(
      (bundle) => bundle.rentalObjectId === objectId
    );
  };

  if (loading) {
    return <LoadingSpinner text="Lade Bundle-Verwaltung..." />;
  }

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 1, md: 3 },
        px: { xs: 1, md: 3 },
        minHeight: "100vh",
        bgcolor: "grey.50",
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 4 },
          mb: { xs: 2, md: 3 },
          background:
            "linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)",
          color: "white",
          borderRadius: { xs: 2, md: 3 },
          boxShadow: "0 8px 32px rgba(25, 118, 210, 0.15)",
          overflow: "hidden",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)",
            pointerEvents: "none",
          },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={{ xs: 1.5, md: 2 }}
          sx={{ position: "relative", zIndex: 1 }}
        >
          <Box
            sx={{
              p: { xs: 1, md: 1.5 },
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              backdropFilter: "blur(10px)",
            }}
          >
            <InventoryIcon
              sx={{
                fontSize: { xs: "1.75rem", md: "2.5rem" },
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
              }}
            />
          </Box>
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontSize: { xs: "1.5rem", md: "2.125rem" },
                fontWeight: 700,
                mb: { xs: 0.5, md: 0.75 },
                textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                lineHeight: 1.2,
              }}
            >
              Bundle-Verwaltung
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                opacity: 0.95,
                fontSize: { xs: "0.875rem", md: "1rem" },
                fontWeight: 400,
                textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                lineHeight: 1.4,
              }}
            >
              Erstellen Sie Bundle-Artikel und weisen Sie sie Mietobjekten zu
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: { xs: 2, md: 3 },
            borderRadius: 2,
            mx: { xs: 0.5, md: 0 },
            fontSize: { xs: "0.875rem", md: "1rem" },
            "& .MuiAlert-icon": {
              fontSize: { xs: "1.25rem", md: "1.5rem" },
            },
            boxShadow: "0 4px 16px rgba(244, 67, 54, 0.2)",
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          sx={{
            width: "100%",
            borderRadius: 2,
            boxShadow: "0 8px 24px rgba(76, 175, 80, 0.3)",
            fontSize: { xs: "0.875rem", md: "1rem" },
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Object Selection Bar */}
      <ObjectSelectionBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedObjects={selectedObjects}
        onSelectAll={handleSelectAll}
        onClearSelection={handleClearSelection}
        onAddBundle={() => setShowCreateModal(true)}
        filteredObjects={filteredObjects}
        totalObjects={rentalObjects.length}
      />

      {/* Content Grid */}
      <Grid
        container
        spacing={{ xs: 2, md: 3 }}
        sx={{ px: { xs: 0.5, md: 0 } }}
      >
        {filteredObjects.map((object) => {
          const objectBundles = getObjectBundles(object.id);
          const isSelected = selectedObjects.has(object.id);

          return (
            <Grid item xs={12} sm={6} lg={4} key={object.id}>
              <Box
                sx={{
                  position: "relative",
                  height: "100%",
                  borderRadius: { xs: 2, md: 3 },
                  overflow: "hidden",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  border: isSelected ? "2px solid" : "2px solid transparent",
                  borderColor: isSelected ? "primary.main" : "transparent",
                  backgroundColor: isSelected
                    ? "rgba(25, 118, 210, 0.04)"
                    : "transparent",
                  "&:hover": {
                    transform: { xs: "none", md: "translateY(-2px)" },
                    boxShadow: {
                      xs: "none",
                      md: "0 8px 25px rgba(0,0,0,0.15)",
                    },
                    "& .selection-checkbox": {
                      opacity: 1,
                      transform: "scale(1)",
                    },
                  },
                }}
              >
                {/* Selection Checkbox */}
                <Checkbox
                  checked={isSelected}
                  onChange={(e) =>
                    handleObjectSelect(object.id, e.target.checked)
                  }
                  className="selection-checkbox"
                  sx={{
                    position: "absolute",
                    top: { xs: 8, md: 12 },
                    right: { xs: 8, md: 12 },
                    zIndex: 2,
                    backgroundColor: isSelected ? "#4caf50" : "white",
                    color: isSelected ? "white" : "primary.main",
                    borderRadius: { xs: 1, md: 1.5 },
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    opacity: { xs: 1, md: isSelected ? 1 : 0.7 },
                    transform: {
                      xs: "scale(1)",
                      md: isSelected ? "scale(1)" : "scale(0.9)",
                    },
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: isSelected ? "#388e3c" : "grey.100",
                      color: isSelected ? "white" : "primary.main",
                      transform: "scale(1.1)",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: { xs: "1.25rem", md: "1.5rem" },
                    },
                  }}
                />

                {/* Bundle List Card */}
                <BundleListCard
                  rentalObject={object}
                  bundles={objectBundles}
                  onRemoveBundle={handleRemoveBundle}
                  onEditBundle={handleEditBundle}
                />
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {/* Empty State */}
      {filteredObjects.length === 0 && !loading && (
        <Paper
          elevation={1}
          sx={{
            p: { xs: 3, md: 6 },
            textAlign: "center",
            borderRadius: { xs: 3, md: 4 },
            background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
            border: "1px solid rgba(0,0,0,0.08)",
            mx: { xs: 0.5, md: 0 },
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "radial-gradient(circle at 50% 50%, rgba(25, 118, 210, 0.05) 0%, transparent 70%)",
              pointerEvents: "none",
            },
          }}
        >
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <InventoryIcon
              sx={{
                fontSize: { xs: 48, md: 72 },
                color: "primary.main",
                opacity: 0.7,
                mb: { xs: 2, md: 3 },
                filter: "drop-shadow(0 4px 8px rgba(25, 118, 210, 0.2))",
              }}
            />
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: "grey.800",
                fontSize: { xs: "1.125rem", md: "1.25rem" },
                mb: { xs: 1.5, md: 2 },
              }}
            >
              {searchTerm
                ? "Keine passenden Objekte gefunden"
                : "Keine Mietobjekte gefunden"}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: { xs: "0.9rem", md: "1rem" },
                lineHeight: 1.6,
                maxWidth: "400px",
                mx: "auto",
              }}
            >
              {searchTerm
                ? `Keine Objekte entsprechen der Suche "${searchTerm}". Versuchen Sie andere Suchbegriffe.`
                : "Es sind noch keine Mietobjekte vorhanden. Erstellen Sie zuerst Mietobjekte, um Bundle-Artikel hinzuzufügen."}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Create Bundle Article Modal */}
      <CreateBundleArticleModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateBundleSuccess}
        selectedObjectIds={Array.from(selectedObjects)}
      />

      {/* Edit Bundle Article Modal */}
      <EditBundleArticleModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingBundle(null);
          setEditingObjectId("");
        }}
        onSuccess={handleEditSuccess}
        bundle={editingBundle}
        objectId={editingObjectId}
      />
    </Container>
  );
};

export default BundleManagementPage;
